<template>
  <div class="sb_sidebar" :class="{ s_active: active, s_loading: loading }">
    <transition name="slide-sidebar" @after-leave="closed = true" @before-enter="closed = false">
      <div v-show="active" class="sb_sidebar_wrapper">
        <div ref="scroller" class="sb_sidebar_scroller">
          <div class="sb_sidebar_scroller_top">
            <SvgIcon class="sb_sidebar_close" @click.native="$emit('update:active', null)" icon="close" />
          </div>
          <div class="sb_sidebar_container">
            <keep-alive>
              <component
                :is="component"
                @close="$emit('update:active', null)"
                :loading.sync="loader"
                :active="activeCache"
                @errorModal="errorModal"
                @addImage="addImage"
                @addBackground="addBackground"
              />
            </keep-alive>
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="active"
        class="sb_sidebar_overlay"
        @drop="drawOnCanvas"
        @dragover.prevent
        @dragenter.prevent
        @click="$emit('update:active', null)"
      />
    </transition>
  </div>
</template>

<script>
import AddBackground from './Background';
import AddText from './Text';
import AddImage from './Image';
import { mapState, mapMutations } from 'vuex';

export default {
  components: {
    AddBackground,
    AddText,
    AddImage,
  },
  methods: {
    ...mapMutations('design', ['setActiveImage', 'removeActiveImage', 'setActiveBackground']),
    ...mapMutations('modal', ['setModalData']),
    ...mapMutations('canvas', ['removeObject']),
    errorModal({ error, type }) {
      if (error === 'fileSize') {
        this.setModalData({
          title: 'Ah oh!',
          content: `Jouw ${type} komt boven het maximaal upload limiet van ${window.$store.imagesMaxSize}MB uit, kies een kleiner bestand.`,
        });
      } else if (error === 'generic') {
        this.setModalData({
          title: 'Woeps!',
          content: `Er ging iets fout met het toevoegen van jouw ${type}, probeer het opnieuw.`,
        });
      }
    },
    limitModal() {
      this.setModalData({
        title: 'Ah oh',
        content:
          'Het lijkt er op dat je het maximimaal aantal afbeeldingen heb bereikt, verwijder er eerst één voordat je een nieuwe toevoegd.',
      });
    },
    cancablePromise(fn) {
      return new Promise((res, rej) => {
        this.rejectPromise = rej;
        fn.then((succes) => {
          res(succes);
        }).catch((error) => {
          rej(error);
        });
      });
    },
    async addImage({ src, id }) {
      //   if (this.activeImages.length > this.IMAGES_MAX_COUNT) {
      //     this.limitModal();
      //     return;
      //   }

      this.$emit('update:loading', id);
      await this.$nextTick();

      let image;
      try {
        this.inProgress = true;

        // await this.cancablePromise(
        //   new Promise((res) => {
        //     setTimeout(() => {
        //       res();
        //     }, 10000000);
        //   }),
        // );

        image = await this.cancablePromise(this.manager.addImage(src, id));

        this.inProgress = false;

        if (id === 'custom-image') {
          this.removeObject(this.customImage);
          this.customImage = image;
        }
      } catch (e) {
        this.inProgress = false;
        this.$emit('update:loading', false);
        if (e !== 'cancel') {
          this.errorModal({ error: 'generic', type: 'afbeelding' });
        }
        return;
      }

      this.$emit('update:loading', false);
      this.$emit('update:active', null);

      if (id === 'custom-image' && this.activeImages.includes(id)) {
        return;
      }

      this.setActiveImage(id);
    },
    drawOnCanvas(e) {
      const type = e.dataTransfer.getData('type');
      const image = JSON.parse(e.dataTransfer.getData('image'));
      const id = e.dataTransfer.getData('id');

      // triggers this.addBackground or this.addImage
      this[`add${type}`]({ src: image.medium, original: image.original, id });
    },
    async addBackground({ src, original, id }) {
      this.$emit('update:loading', id);
      let background;
      try {
        this.inProgress = true;
        background = await this.cancablePromise(this.manager.setBackground(src, id, original));
        background.data.original = original;
        this.inProgress = false;
      } catch (e) {
        this.inProgress = false;
        this.$emit('update:loading', false);
        if (e !== 'cancel') {
          this.errorModal({ error: 'generic', type: 'afbeelding' });
        }
        return;
      }

      this.$emit('update:loading', false);
      this.setActiveBackground(id);
      this.$emit('addBackground');

      this.$emit('update:active', null);

      return background;
    },
    objectClicked(e) {
      if (this.active) {
        return;
      }

      if (!e || !e.detail) {
        return;
      }

      if (e.detail.get('type') === 'text') {
        this.$emit('update:active', 'text');
      }
    },
    checkRemove({ detail }) {
      const { id } = detail;
      if (this.activeImages.includes(id)) {
        this.removeActiveImage(id);
      } else if (this.activeBackground === id) {
        this.setActiveBackground(null);
      }
    },
  },
  async mounted() {
    window.addEventListener('object:removed', this.checkRemove);
    window.addEventListener('object:clicked', this.objectClicked);

    if (window.$store.activeBackground) {
      await this.addBackground({
        src: window.$store.activeBackground.medium,
        original: window.$store.activeBackground.original,
        id: window.$store.activeBackground.id,
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('object:removed', this.checkRemove);
    window.removeEventListener('object:clicked', this.objectClicked);
  },
  data() {
    return {
      activeCache: null,
      closed: true,
      IMAGES_MAX_COUNT: this.$root.imagesMaxCount,
      rejectPromise: null,
      customImage: null,
      inProgress: false,
    };
  },
  props: {
    active: {
      type: String,
      default: null,
    },
    loading: {
      type: [String, Boolean],
      default: false,
    },
  },
  watch: {
    async active(val) {
      if (val) {
        this.activeCache = val;

        await this.$nextTick();
        this.$refs.scroller.scrollTop = 0;
      }
    },
    async loading(val) {
      if (!val && this.inProgress) {
        this.rejectPromise('cancel');
      }
    },
  },
  computed: {
    ...mapState('onboarding', { onboardingActive: (state) => state.active }),
    ...mapState('canvas', ['manager']),
    ...mapState('design', {
      activeBackground: (state) => {
        return state.background;
      },
      activeImages: (state) => {
        return state.images;
      },
    }),
    loader: {
      get() {
        return this.loading;
      },
      set(val) {
        this.$emit('update:loading', val);
      },
    },
    activeBar() {
      if (this.closed) {
        return this.active;
      }
      return this.activeCache;
    },
    component() {
      const { activeBar } = this;
      if (!activeBar) {
        return null;
      }
      return `Add${activeBar.charAt(0).toUpperCase() + activeBar.substring(1)}`;
    },
  },
};
</script>

<style lang="scss">
#sb_fotowand-designer {
  .sb_sidebar {
    width: 100%;
    height: calc(100% - (10rem + 4px));
    left: 0;
    top: auto;
    bottom: calc(10rem + 4px);
    position: absolute;
    z-index: 999;
    display: flex;
    flex-direction: column;
    pointer-events: none;
    @include media('>=tiny') {
      height: calc(100% - (8rem + 4px));
      bottom: calc(8rem + 4px);
    }
    @include media('>=small') {
      bottom: calc(9rem + 4px);
      height: calc(100% - (9rem + 4px));
    }
    @include media('>=medium-portrait') {
      bottom: auto;
      top: 0;
      height: calc(100% - (6rem + 4px));
      flex-direction: row;
    }
    @include media('>=medium') {
      height: calc(100% - (7rem - 4px));
      flex-direction: row;
    }
    &.s_active {
      pointer-events: all;
    }
    &.s_loading {
      .sb_sidebar_close {
        opacity: 0.6;
        pointer-events: none;
      }
      .sb_sidebar_overlay {
        pointer-events: none;
      }
    }
    &_scroller {
      overscroll-behavior: contain;
      overflow: auto;
      padding-bottom: 6rem;
      width: 100%;
      &_top {
        height: 2rem;
        position: sticky;
        display: block;
        z-index: 9;
        top: 0;
        left: 0;
        width: 100%;
        background-image: linear-gradient(180deg, rgba($brand-white, 1) 1rem, rgba($brand-white, 0));
        @include media('>medium-portrait') {
          height: 3rem;
        }
      }
    }
    &_container {
      padding: 0 1rem 2rem;
      @include media('>medium-portrait') {
        padding: 0 1.4rem 2rem;
      }
    }
    &_wrapper {
      height: 50%;
      width: 100%;
      bottom: 0;
      position: relative;
      background: $brand-white;
      display: flex;
      flex-shrink: 0;
      @include media('>=medium-portrait') {
        top: 0;
        height: 100%;
        width: 20rem;
      }
      @include media('>=medium') {
        width: 24rem;
      }
      @include media('>=large') {
        width: 26rem;
      }
      &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 100%;
        width: 100%;
        height: 14px;
        background-image: linear-gradient(0deg, rgba($brand-black, 0.15), rgba($brand-black, 0));
        @include media('>=medium-portrait') {
          left: 100%;
          top: 0;
          bottom: auto;
          width: 14px;
          height: 100%;
          background-image: linear-gradient(90deg, rgba($brand-black, 0.15), rgba($brand-black, 0));
        }
      }
    }
    &_overlay {
      height: 100%;
      width: 100%;
      flex-grow: 1;
      cursor: s-resize;
      order: -1;
      @include media('>=medium-portrait') {
        order: 0;
        cursor: w-resize;
      }
    }
    &_close {
      position: absolute;
      right: 0;
      top: 0;
      padding: 0.8rem;
      margin: 0.4rem;
      z-index: 999;
      background: $brand-white;
      border-radius: 50%;
      font-size: 1rem;
      cursor: pointer;
      color: $brand-primary;
      @include media('>=medium') {
        font-size: 1.1rem;
        margin: 0.4rem;
        padding: 0.8rem;
      }
    }
  }
}
</style>
